import React, { useState, useCallback, useMemo, memo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useGoogleLogin } from '@react-oauth/google';
import { register, googleAuth } from '../../api/auth';
import { useAuth } from '../../contexts/AuthContext';

import styles from './styles/Auth.module.css';

const Register = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setIsAuthenticated } = useAuth();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
    });
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isEmailSent, setIsEmailSent] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showEmailForm, setShowEmailForm] = useState(false);

    const isFormValid = useMemo(() => {
        return formData.firstName && formData.email && formData.password;
    }, [formData]);

    const handleSubmit = useCallback(async e => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await register(formData);
            setIsEmailSent(true);
        } catch (error) {
            // WAITLIST_PHASE
            if (error.message === 'invite_only') {
                setError(<div>
                    JetChats is invite only. Please join the waitlist: <a href="/waitlist">https://jetchats.com/waitlist</a>
                </div>);
            } else {
                setError(error.message);
            }
        } finally {
            setIsLoading(false);
        }
    }, [formData]);

    const handleGoogleSuccess = useCallback(async tokenResponse => {
        setError('');
        setIsLoading(true);

        try {
            // Get user profile with the access token
            const profileResponse = await fetch(`https://www.googleapis.com/oauth2/v3/userinfo?access_token=${tokenResponse.access_token}`);
            const profileData = await profileResponse.json();

            // Use the ID token from the profile data
            if (profileData.sub) {
                await googleAuth(tokenResponse.access_token);
                setIsAuthenticated(true);
                const from = location.state?.from;
                const isAuthPage = from?.pathname === '/login' || from?.pathname === '/register';
                const redirectPath = !from || isAuthPage
                    ? '/chats'
                    : `${from.pathname || ''}${from.search || ''}${from.hash || ''}`;
                navigate(redirectPath, { replace: true });
            } else {
                setError('Failed to get Google profile');
            }
        } catch (error) {
            if (error.message === 'invite_only') {
                setError(<div>
                    JetChats is invite only. Please join the waitlist: <a href="/waitlist">https://jetchats.com/waitlist</a>
                </div>);
            } else {
                setError(error.message || 'Google authentication failed');
            }
        } finally {
            setIsLoading(false);
        }
    }, [location.state, navigate, setIsAuthenticated]);

    const handleGoogleError = useCallback(() => {
        setError('Google sign-up failed. Please try again.');
    }, []);

    const loginButton = useGoogleLogin({
        onSuccess: tokenResponse => handleGoogleSuccess(tokenResponse),
        onError: error => handleGoogleError(error),
        scope: 'email profile',
    });

    const handleEmailRegisterClick = useCallback(() => {
        setShowEmailForm(true);
        setError('');
    }, []);

    const handleChange = useCallback(e => {
        const value = e.target.name === 'email' ? e.target.value.toLowerCase() : e.target.value;

        setFormData(prev => ({
            ...prev,
            [e.target.name]: value,
        }));
    }, []);

    if (isEmailSent) {
        return (
            <div className={styles.container}>
                <Link to="/">
                    <img 
                        src="/icons/android-icon-192x192.png" 
                        alt="JetChats Logo" 
                        className={styles.logo}
                    />
                </Link>
                <div className={styles.card}>
                    <h2>Verify Email</h2>
                    <p className={styles.text}>We've sent you a verification link to {formData.email}</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <Link to="/">
                <img 
                    src="/icons/android-icon-192x192.png" 
                    alt="JetChats Logo" 
                    className={styles.logo}
                />
            </Link>
            <div className={styles.card}>
                <h2>Create an account</h2>

                <button onClick={loginButton} className={styles.googleButton}>
                    <span>Continue with Google</span>
                </button>

                {!showEmailForm ? (
                    <div className={styles.emailRegisterSection}>
                        <div className={styles.divider}>
                            <span>OR</span>
                        </div>
                        <button 
                            onClick={handleEmailRegisterClick} 
                            className={styles.emailRegisterButton}
                            type="button"
                        >
                            Sign up with Email
                        </button>
                        <p className={styles.signInText}>
                            Already registered? <Link to="/login" state={location.state}>Sign in</Link>
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.nameRow}>
                        <div className={styles.formGroup}>
                            <label htmlFor="firstName">First Name</label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                placeholder="First name"
                                required
                            />
                        </div>
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Enter your email address"
                            required
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="password">Password</label>
                        <div className={styles.passwordInput}>
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="••••••••"
                                required
                                minLength={8}
                            />
                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        </div>
                    </div>
                    <div className={styles.buttonGroup}>
                        <button
                            type="button"
                            className={styles.backButton}
                            onClick={() => {
                                setShowEmailForm(false);
                                setError('');
                            }}
                        >
                            Back
                        </button>
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isLoading || !isFormValid}
                        >
                            {isLoading ? 'Creating account...' : 'Sign up'}
                        </button>
                    </div>
                    {error && <div className={styles.error}>{error}</div>}
                    <p className={styles.signInText}>
                        Already registered? <Link to="/login" state={location.state}>Sign in</Link>
                    </p>
                    <div className={styles.termsText}>
                        <p>
                            By using JetChats you agree to the <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>
                        </p>
                    </div>
                </form>
                )}
            </div>
        </div>
    );
};

export default memo(Register);
